import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PhoneGateForm } from "@/components/auth/PhoneGateForm";
import { Button } from "@/components/ui/button";
import { ApiError, apiFetch, authApi } from "@/lib/api";
import { needsGooglePhoneGate } from "@/lib/phoneGate";
import { trackFunnel } from "@/lib/analytics";
import { useOnboardingStore } from "@/stores/onboarding";

export const Route = createFileRoute("/onboarding/done")({
  component: DoneStep,
});

function DoneStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const personas = useOnboardingStore((s) => s.personas);
  const channels = useOnboardingStore((s) => s.channels);
  const language = useOnboardingStore((s) => s.language);
  const alertLanguage = useOnboardingStore((s) => s.alertLanguage);
  const consent = useOnboardingStore((s) => s.consent);
  const setFinishedLater = useOnboardingStore((s) => s.setFinishedLater);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsPhone, setNeedsPhone] = useState<boolean | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function checkUser() {
      try {
        const me = await authApi.me();
        if (cancelled) return;
        setNeedsPhone(needsGooglePhoneGate(me.user));
      } catch {
        if (!cancelled) setNeedsPhone(false);
      }
    }
    void checkUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const channelList = Object.entries(channels)
    .filter(([, v]) => v)
    .map(([k]) => k.toUpperCase())
    .join(", ");

  async function finish() {
    if (needsPhone && !phoneVerified) return;

    trackFunnel("step_completed", "done");
    setFinishedLater(false);
    setError(null);
    setSaving(true);
    try {
      await apiFetch("/user/onboarding", {
        method: "PATCH",
        audience: "user",
        body: JSON.stringify({
          complete: true,
          personas,
          language: language === "sw" ? "SW" : "EN",
          messageLanguage: alertLanguage === "sw" ? "sw" : "en",
          channelsSms: channels.sms,
          channelsEmail: channels.email,
          channelsWhatsapp: channels.whatsapp,
          channelsPush: channels.push,
          whatsappOptIn: channels.whatsapp,
          consentFlags: { ...consent },
          consentTypes: [
            "place_storage",
            ...(consent["live_location_while_open"] ? ["live_location_while_open"] : []),
          ],
        }),
      });
      void navigate({ to: "/app/home" });
    } catch (err) {
      if (err instanceof ApiError && (err.code === "GOOGLE_NO_PHONE" || err.code === "PHONE_NOT_VERIFIED")) {
        setNeedsPhone(true);
        setPhoneVerified(false);
        setError(t("onboarding.phoneGate.body"));
        return;
      }
      setError(err instanceof ApiError ? err.message : t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  if (needsPhone === null) {
    return (
      <div className="flex flex-1 flex-col gap-4" aria-busy="true" aria-label={t("common.loading")}>
        <div className="h-12 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-1/2 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (needsPhone && !phoneVerified) {
    return (
      <div className="flex flex-1 flex-col">
        <h1 className="font-display text-3xl font-bold">{t("onboarding.phoneGate.title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {t("onboarding.phoneGate.body")}
        </p>

        <div className="mt-8">
          <PhoneGateForm onVerified={() => setPhoneVerified(true)} />
        </div>

        <div className="mt-auto space-y-2 pt-10">
          <Button className="w-full" disabled>
            {t("onboarding.phoneGate.goHome")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h1 className="font-display text-3xl font-bold">{t("onboarding.done.title")}</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {t("onboarding.done.body", {
          persona: personas.join(", ") || t("onboarding.persona.resident"),
          channels: channelList || t("onboarding.done.noChannels"),
        })}
      </p>

      {phoneVerified ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          {t("onboarding.phoneGate.verified")}
        </p>
      ) : null}

      <div className="mt-6 space-y-2 text-sm">
        <EditRow label={t("onboarding.done.persona")} onEdit={() => void navigate({ to: "/onboarding/persona" })} />
        <EditRow label={t("onboarding.done.alerts")} onEdit={() => void navigate({ to: "/onboarding/alerts" })} />
      </div>

      {error ? (
        <p role="alert" className="mt-4 rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
          {error}
        </p>
      ) : null}

      <div className="mt-auto space-y-2 pt-10">
        <Button
          className="w-full"
          disabled={saving || (needsPhone === true && !phoneVerified)}
          onClick={() => void finish()}
        >
          {saving ? t("common.saving") : t("onboarding.phoneGate.goHome")}
        </Button>
        {!needsPhone ? (
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setFinishedLater(true);
              trackFunnel("abandoned", "done");
              void navigate({ to: "/app/home" });
            }}
          >
            {t("onboarding.done.finishLater")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function EditRow({ label, onEdit }: { label: string; onEdit: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
      <span className="font-semibold">{label}</span>
      <Button size="sm" variant="ghost" onClick={onEdit}>
        {t("common.edit")}
      </Button>
    </div>
  );
}
